/**
 * Canonical file-size formatter for the app.
 *
 * See `docs/LIBRARY_STANDARDS.md` — this is the shared helper referenced there.
 * Do not add local copies of this function, and do not use the `filesize` package.
 *
 * Input is always **bytes**. If you have kilobytes, multiply by 1024 at the call site.
 */

const UNITS = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB'] as const;
const BASE = 1024;

/**
 * Format a byte count for display.
 *
 * @param bytes - Size in bytes.
 * @returns Formatted string, e.g. `"1.5 KB"`, `"10 MB"`, `"0 Bytes"`.
 *
 * @example
 * formatFileSize(0);        // "0 Bytes"
 * formatFileSize(1536);     // "1.5 KB"
 * formatFileSize(10485760); // "10 MB"
 */
export function formatFileSize(bytes: number): string {
  if (typeof bytes !== 'number' || !Number.isFinite(bytes) || bytes <= 0) {
    return '0 Bytes';
  }

  // Clamp the exponent so sizes beyond the largest known unit still render sensibly
  // rather than producing "NaN undefined".
  const exponent = Math.min(
    Math.floor(Math.log(bytes) / Math.log(BASE)),
    UNITS.length - 1
  );

  const value = bytes / Math.pow(BASE, exponent);

  // Two decimal places, but drop trailing zeros: 1.5 not 1.50, 10 not 10.00.
  return `${Math.round(value * 100) / 100} ${UNITS[exponent]}`;
}

export default formatFileSize;
